/**
 * Time and trading days.
 *
 * Every timestamp is stored as UTC ISO-8601 with milliseconds. Every DAY
 * BOUNDARY — trading day, shift day, report range, rollup key — is computed in
 * the business timezone, never in UTC.
 *
 * This distinction is not pedantry. A café in Asia/Manila closing at 21:00 local
 * is 13:00 UTC; bucketing its sales by UTC date would split a single trading
 * evening across two report days and make the Z reading disagree with the
 * drawer. FR-097.
 */

export type IsoTimestamp = string;
/** YYYY-MM-DD in the business timezone. */
export type BusinessDate = string;

export function nowIso(): IsoTimestamp {
  return new Date().toISOString();
}

export function toIso(date: Date): IsoTimestamp {
  return date.toISOString();
}

/**
 * The trading date a moment belongs to, in the business timezone.
 *
 * Uses Intl rather than manual offset arithmetic so daylight saving and any
 * future zone change are handled by the platform's tz database rather than by
 * an assumption baked in here.
 */
export function businessDate(instant: Date | IsoTimestamp, timezone: string): BusinessDate {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** The UTC instant at which a business-local wall-clock time occurs. */
function zonedTimeToUtc(datePart: BusinessDate, timePart: string, timezone: string): Date {
  // Probe: interpret as UTC, measure how the zone renders it, correct by the gap.
  const probe = new Date(`${datePart}T${timePart}Z`);
  const rendered = new Date(probe.toLocaleString("en-US", { timeZone: timezone }));
  const asUtc = new Date(probe.toLocaleString("en-US", { timeZone: "UTC" }));
  const offset = rendered.getTime() - asUtc.getTime();
  return new Date(probe.getTime() - offset);
}

/** Half-open [start, end) UTC range covering one business day. */
export function dayRange(
  date: BusinessDate,
  timezone: string,
): { start: IsoTimestamp; end: IsoTimestamp } {
  const start = zonedTimeToUtc(date, "00:00:00.000", timezone);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}

/** Half-open [start, end) UTC range covering an inclusive span of business days. */
export function rangeBetween(
  from: BusinessDate,
  to: BusinessDate,
  timezone: string,
): { start: IsoTimestamp; end: IsoTimestamp } {
  return {
    start: dayRange(from, timezone).start,
    end: dayRange(to, timezone).end,
  };
}

/** Hour 0–23 in the business timezone, for hourly distribution reporting. */
export function businessHour(instant: Date | IsoTimestamp, timezone: string): number {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  const hour = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    hour12: false,
  }).format(date);
  return Number.parseInt(hour, 10);
}

export function secondsBetween(a: IsoTimestamp, b: IsoTimestamp): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000);
}

/**
 * Clock skew between this device and the server (FR-050).
 *
 * Terminal clocks are never trusted for ordering across terminals. This exists
 * so a record created under a badly wrong clock can be flagged rather than
 * silently mis-dated into the wrong trading day.
 */
export function clockSkewMs(deviceNow: Date, serverNow: IsoTimestamp): number {
  return deviceNow.getTime() - new Date(serverNow).getTime();
}

export const CLOCK_SKEW_THRESHOLD_MS = 120_000;

export function isSkewSignificant(skewMs: number): boolean {
  return Math.abs(skewMs) > CLOCK_SKEW_THRESHOLD_MS;
}
